import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Section } from 'theSource/components';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';
import {
  getFormField,
  getInitialValues,
  getValidationSchema,
} from 'theSource/utils';
import { FormFieldsCategory, FormValues } from 'theSource/types';
import { getFilteredCategories } from './utils';
import {
  ENROLL_IN_MEDICARE_FIELD_NAME,
  LIVES_AT_HOME_FIELD_NAME,
  VERIFIED_FIELD_NAME,
} from 'theSource/features/FamilyMembers/utils';
import './MyFamilyMemberForm.css';

export interface MyFamilyMemberFormProps {
  categories: FormFieldsCategory[];
  isLoading: boolean;
  onSubmitClick: (values: FormValues, resetForm: () => void) => void;
  onCancelClick: () => void;
  values?: { [key: string]: any };
}

export const MyFamilyMemberForm = ({
  categories,
  isLoading,
  onSubmitClick,
  onCancelClick,
  values,
}: MyFamilyMemberFormProps) => {
  const { t } = useTranslation();
  const [livesAtHome, setLivesAtHome] = useState(
    Boolean(values?.[LIVES_AT_HOME_FIELD_NAME])
  );
  const [isEnrolledInMedicare, setIsEnrolledInMedicare] = useState(
    Boolean(values?.[ENROLL_IN_MEDICARE_FIELD_NAME])
  );
  const [isVerified, setIsVerified] = useState(
    Boolean(values?.[VERIFIED_FIELD_NAME])
  );

  const filteredCategories = useMemo(
    () =>
      getFilteredCategories(
        categories,
        livesAtHome,
        isEnrolledInMedicare,
        isVerified
      ),
    [categories, livesAtHome, isEnrolledInMedicare, isVerified]
  );

  const initialValues = useMemo(
    () => getInitialValues(categories),
    [categories]
  );

  const validationSchema = useMemo(
    () => getValidationSchema(filteredCategories),
    [filteredCategories]
  );

  const handleChange = (values: FormValues) => {
    const livesAtHomeNextValue = values.lives_at_home as boolean;
    const enrollInMedicareNextValue = values.enroll_in_medicare as boolean;
    const verifiedNextValue = values.verified as boolean;
    setLivesAtHome(livesAtHomeNextValue);
    setIsEnrolledInMedicare(enrollInMedicareNextValue);
    setIsVerified(verifiedNextValue);
  };

  return (
    <>
      <div className="mt-4">
        <RequiredLegend />
      </div>
      <Form<FormValues>
        testId="family-member-form"
        isLoading={isLoading}
        onSubmitClick={onSubmitClick}
        onCancelClick={onCancelClick}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onChange={handleChange}
      >
        {filteredCategories.map((category) => (
          <Section key={category.name} name={category.name}>
            {category.fields.map(getFormField)}
          </Section>
        ))}
      </Form>
    </>
  );
};
